import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { API_ENDPOINTS, BASE_URL } from './constants';
import { authEventManager } from './authEventManager';


// --- Types (Keep your existing types) ---
export interface ApiResponse<T = any> {
    code: number;
    success: boolean;
    message: string;
    data: T;
    status: 'success' | 'failed';
    timestamp: string;
}

// --- Axios Instance ---
export const api = axios.create({
    baseURL: BASE_URL,
});


// Request Interceptor: Attach the token to every request
api.interceptors.request.use(
    (config) => {
        config.headers["ngrok-skip-browser-warning"] = true
        const token = localStorage.getItem('accessToken');
        // console.log({ config });

        if (token && !config.headers['Authorization'] && config?.url && ![API_ENDPOINTS.AUTH.LOGIN, API_ENDPOINTS.AUTH.REFRESH, API_ENDPOINTS.AUTH.LOGOUT].includes(config?.url as any)) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        if (!config.headers['Content-Type']) {
            config.headers['Content-Type'] = 'application/json';
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response Interceptor: The core of our 401 handling
api.interceptors.response.use(
    (response) => {
        // Any status code that lie within the range of 2xx cause this function to trigger
        return response; // Simply return the successful response
    },
    async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };
        const AUTH_URLS = [API_ENDPOINTS.AUTH.LOGIN]

        // Check for 401 Unauthorized
        if (error.response?.status === 401 && error.response?.status === 401 && originalRequest && !originalRequest._retry) {
            if ([API_ENDPOINTS.AUTH.REFRESH, API_ENDPOINTS.AUTH.LOGOUT].includes(originalRequest?.url as any)) {
                localStorage.clear()
                window.location.replace("/auth/login")
            }
            else {
                // Avoid multiple modals if many APIs fail at once
                const refreshToken = localStorage.getItem('refreshToken');
                const refreshResponse: any = await axios.post<{ accessToken: string }>(
                    `${BASE_URL}${API_ENDPOINTS.AUTH.REFRESH}`,
                    { refreshToken } // Send the refresh token in the body
                );

                // console.log({ refreshResponse });
                // return

                const newAccessToken = refreshResponse.data?.data?.tokens?.access?.token;
                const newRefreshToken = refreshResponse.data?.data?.tokens?.refresh?.token;

                // 2. Store the new access token
                localStorage.setItem('accessToken', newAccessToken);
                localStorage.setItem('refreshToken', newRefreshToken);

                // 3. Update the Authorization header for the current failed request
                api.defaults.headers.common['Authorization'] = `Bearer ${newAccessToken}`;
                originalRequest.headers!.Authorization = `Bearer ${newAccessToken}`;

                // 5. Retry the original request
                return api(originalRequest);
            }
        }
        else if (error.response?.status === 429) {
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
        }

        // For all other errors, just reject the promise
        return Promise.reject(error.response?.data || error);
    }
);


// --- Simplified API Client ---
export const apiClient = {
    get: <T>(endpoint: string, options?: AxiosRequestConfig) =>
        api.get<ApiResponse<T>>(endpoint, options).then(res => res.data).catch(e => e),

    post: <T>(endpoint: string, body: any, options?: AxiosRequestConfig) =>
        api.post<ApiResponse<T>>(endpoint, body, options).then(res => res.data).catch(e => e),

    put: <T>(endpoint: string, body: any, options?: AxiosRequestConfig) =>
        api.put<ApiResponse<T>>(endpoint, body, options).then(res => res.data).catch(e => e),

    delete: <T>(endpoint: string, options?: AxiosRequestConfig) =>
        api.delete<ApiResponse<T>>(endpoint, options).then(res => res.data).catch(e => e),
};